import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { v4 as uuid } from 'uuid';
import { ExcelFile, ExcelSheet, ExcelColumn } from 'react-xlsx-wrapper';
import CommonHeader from '../../util_components/common_header';
import CustomModal from '../../util_components/custom_modal';
import CustomSingleSelect from '../../util_components/custom_single_select';

import { image_error_replace } from '../../../util_functions';
import {
  day_format,
  time_format,
  conciergeBucketDownloadList,
  conciergeBucketOptions,
  conciergeBucketSortOptions,
  EthnicityOptions,
  SALES_ADVISOR_TOOLTIP
} from '../../../constants';
import { get_api, post_api, put_api } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';
import { get_api_url } from '../../../utils/urls';
import SmsWindow from './sms_window';
import './concierge_post_trial_buckets.css';

import UserRole from '../../../myt_chat/chat/enums/UserRole';
import { openChatWithUserUUID } from '../../chat/chat';
import { EditIcon } from '../../../utils/svg';

// Humanize the post-trial bucket enum (backend) into the display label.
const BUCKET_LABELS = {
  POST_TRIAL_NOT_RESET: 'Post Trial Not reset',
  POST_TRIAL_RESET: 'Post Trial Reset',
  POST_TRIAL_BOOKED: 'Post Trial booked'
};
// CSS modifier per bucket so the summary pills + cell badges get a distinct color.
const BUCKET_CSS = {
  POST_TRIAL_NOT_RESET: 'b1',
  POST_TRIAL_RESET: 'b2',
  POST_TRIAL_BOOKED: 'b3'
};
const humanize_bucket = (b) => (b ? BUCKET_LABELS[b] || b : '--');

// PROD-6988: defined rank for the "Bucket name" sort — NOT_RESET → RESET → BOOKED
// (lifecycle order). Unknown/empty buckets sort last.
const BUCKET_RANK = {
  POST_TRIAL_NOT_RESET: 0,
  POST_TRIAL_RESET: 1,
  POST_TRIAL_BOOKED: 2
};
const bucket_rank = (b) => (b in BUCKET_RANK ? BUCKET_RANK[b] : 99);

// Humanize the history event_type for the timeline.
const EVENT_LABELS = {
  BUCKET_CHANGE: 'Bucket change',
  CONCIERGE_ASSIGN: 'Concierge assign',
  CONCIERGE_REASSIGN: 'Concierge reassign',
  CONCIERGE_RETAINED: 'Concierge retained'
};
const humanize_event = (e) => (e ? EVENT_LABELS[e] || e : '');

class ConciergePostTrialBuckets extends Component {
  state = {
    loading: true,
    students: [],
    history: '',
    history_open: '',
    open_info: false,
    student_info: {},
    selected_iana_timezone: 'ALL',
    sms_window_open: false,
    sms_student: '',
    sms_student_name: '',
    iana_abbr_list: [],
    original_message_uuid: '',
    pods: [],
    selected_pod: 'ALL',
    tz_region_map: {},
    open_ethnicity_popup: false,
    concierge_entered_ethnicity: '',
    message_dispatch_method: '',
    is_allowed_whatsapp: false,
    is_allowed_sms: false,
    first_1on1_filter: 'ALL',
    selected_bucket: 'ALL',
    sort_by: 'bucket_assignment_date',
    bucket_history_open: false,
    bucket_history_timeline: [],
    bucket_history_student_name: '',
    history_loading: false
  };

  async componentDidMount() {
    // Widen the shared shell container for THIS page only. common.css caps every
    // .common-page-container at width:calc(90% - 60px) + 5% side margins, which
    // starves the content. Reverted on unmount, so no other page is affected.
    const cpc = document.querySelector('.common-page-container');
    if (cpc) cpc.classList.add('cb-fullwidth');

    this.load_data();
    this.load_pods();
  }

  componentWillUnmount() {
    const cpc = document.querySelector('.common-page-container');
    if (cpc) cpc.classList.remove('cb-fullwidth');
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname.toString() !== this.props.location.pathname.toString()) {
      this.clear_timezone();
      this.clear_pod();
      this.clear_first_1on1();
      this.clear_bucket();
      this.load_data();
    }
  }

  load_pods = () => {
    // Pods are the PROD-6787 regions — used here only to label the pod dropdown (id → name).
    // Students are bucketed into pods by timezone via resolve_pod_timezones (the exact
    // assignment-engine logic), NOT by the regions' country lists. Best-effort: on error we
    // leave pods empty so the rest of the report keeps working.
    get_api(get_api_url('/v2/cms_user/concierge_pods/regions'), true)
      .then((res) => {
        this.setState({ pods: (res.data && res.data.data) || [] });
      })
      .catch((e) => {
        let err_message;
        try {
          err_message = e.response.data.message;
        } catch (err) {
          err_message = 'Failed to load pods.';
        }
        this.props.set_notification_variable(true, 'error', err_message);
      });
  };

  load_data = () =>
    // Clear tz_region_map with students; resolve_pod_timezones repopulates it once the new cohort
    // loads, so a selected pod never filters new students against the previous cohort's map.
    this.setState({ loading: true, students: [], tz_region_map: {} }, () => {
      // PROD-6988: bucket report list (apis, un-enveloped — read res.data.*).
      const url = get_api_url('get_concierge_bucket_students');
      // No date filters on this report: send no date params so the backend returns its
      // default window (the last-30-day cohort).
      const payload = {
        iana_timezone: this.props.iana_timezone,
        is_back_pain_client: 0
      };
      post_api(url, payload, true)
        .then((res) => {
          const student_data = res.data.student_data || [];
          this.setState(
            {
              students: [...student_data],
              iana_abbr_list: [...(res.data.iana_abbr_list || [])]
            },
            () => {
              this.setState({ loading: false });
              this.resolve_pod_timezones(student_data);
            }
          );
        })
        .catch((e) => {
          let err_message;
          try {
            err_message = e.response.data.message;
          } catch (err) {
            err_message = 'Some error occured. Please contact dev team.';
          }
          this.props.set_notification_variable(true, 'error', err_message);
          this.setState({ loading: false });
        });
    });

  change_iana_timezone = (e) => {
    this.setState({ selected_iana_timezone: e.target.value });
  };

  change_pod = (e) => {
    this.setState({ selected_pod: e.target.value });
  };

  change_first_1on1 = (e) => this.setState({ first_1on1_filter: e.target.value });

  clear_first_1on1 = () => this.setState({ first_1on1_filter: 'ALL' });

  change_bucket = (e) => this.setState({ selected_bucket: e.target.value });

  clear_bucket = () => this.setState({ selected_bucket: 'ALL' });

  change_sort = (e) => this.setState({ sort_by: e.target.value });

  // Clear resets to the default sort (server default: latest-modified bucket row on top).
  clear_sort = () => this.setState({ sort_by: 'bucket_assignment_date' });

  get_bucket_options = () => [{ value: 'ALL', label: 'ALL' }, ...conciergeBucketOptions];

  // Total finished + scheduled sessions a card shows (yoga + group, fin/sch).
  // Mirrors render_mobile_student_card's Sessions cell quantities.
  total_sessions = (s) =>
    (s.yoga_sessions_finshed || 0) +
    (s.group_sessions_finshed || 0) +
    (s.yoga_sessions_scheduled || 0) +
    (s.group_sessions_scheduled || 0);

  // Numeric epoch for a date-ish value; non-dates -> -Infinity so they sink to the
  // bottom under our DESC date sorts (modified_date / trial_end_date) instead of floating up.
  to_time = (d) => {
    const t = d ? new Date(d).getTime() : NaN;
    return Number.isNaN(t) ? -Infinity : t;
  };

  // Pure comparator keyed by sort_by. Default = modified_date DESC (matches the server
  // default ordering, "Bucket assignment date"). Does not mutate inputs.
  sort_comparator = (sort_by) => {
    const cmp_str = (a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });
    switch (sort_by) {
      case 'name':
        return (a, b) => cmp_str(a.name, b.name);
      case 'timezone':
        return (a, b) => cmp_str(a.iana_timezone, b.iana_timezone);
      case 'sales_advisor_name': {
        // Blanks (no advisor) sort last; otherwise A→Z.
        const norm = (s) => {
          const v = s.sales_advisor_name;
          return !v || v === '--' ? '' : v;
        };
        return (a, b) => {
          const av = norm(a);
          const bv = norm(b);
          if (!av && !bv) return 0;
          if (!av) return 1;
          if (!bv) return -1;
          return cmp_str(av, bv);
        };
      }
      case 'trial_end_date':
        return (a, b) => this.to_time(b.trial_end_date) - this.to_time(a.trial_end_date);
      case 'is_member':
        // Members (is_member === 1) first.
        return (a, b) => (b.is_member ? 1 : 0) - (a.is_member ? 1 : 0);
      case 'sessions':
        return (a, b) => this.total_sessions(b) - this.total_sessions(a);
      case 'replied':
        // "Replied" uses the same signal as the card's Replied cell (to_student == 1).
        return (a, b) => (b.to_student == 1 ? 1 : 0) - (a.to_student == 1 ? 1 : 0);
      case 'bucket_name':
        return (a, b) => bucket_rank(a.current_bucket) - bucket_rank(b.current_bucket);
      case 'bucket_assignment_date':
      default:
        // Default: newest bucket-row modified_date first (server default).
        return (a, b) => this.to_time(b.modified_date) - this.to_time(a.modified_date);
    }
  };

  // Stable, non-mutating sort of the already-filtered list. Stability via index tiebreak so
  // equal-key rows keep their server (modified_date DESC) order under non-date sorts.
  sort_students = (filter_students) => {
    const cmp = this.sort_comparator(this.state.sort_by);
    return filter_students
      .map((s, i) => [s, i])
      .sort((x, y) => {
        const r = cmp(x[0], y[0]);
        return r !== 0 ? r : x[1] - y[1];
      })
      .map((pair) => pair[0]);
  };

  // Human label for the first-1on1 session status column in the download.
  get_first_1on1_status_label = (status) => {
    const map = {
      FINISHED: 'Finished',
      SCHEDULED: 'Scheduled',
      CANCELLED: 'Cancelled',
      STUDENT_NO_SHOW: 'Student no show',
      TEACHER_NO_SHOW: 'Teacher no show',
      STUDENT_AND_TEACHER_NO_SHOW: 'Student & teacher no show'
    };
    return status ? map[status] || status : 'Not booked';
  };

  // Format entered_bucket_at in the ops user's timezone (matches the rest of the report).
  fmt_bucket_date = (d) => {
    if (!d) return '--';
    return moment(d).tz(this.props.iana_timezone).format(day_format);
  };

  open_bucket_history = (student) => {
    // Use a modal-local history_loading spinner so this never hijacks the page-wide
    // `loading` (which drives CommonHeader and would blank the whole list).
    this.setState(
      {
        bucket_history_open: true,
        bucket_history_timeline: [],
        bucket_history_student_name: student.name,
        history_loading: true
      },
      () => {
        const url = get_api_url('get_concierge_bucket_history');
        post_api(url, { student_uuid: student.uuid }, true)
          .then((res) => {
            this.setState({
              history_loading: false,
              bucket_history_timeline: (res.data && res.data.timeline) || []
            });
          })
          .catch((e) => {
            let err_message;
            try {
              err_message = e.response.data.message;
            } catch (err) {
              err_message = 'Some error occured. Please contact dev team.';
            }
            this.props.set_notification_variable(true, 'error', err_message);
            this.setState({ history_loading: false });
          });
      }
    );
  };

  close_bucket_history = () =>
    this.setState({
      bucket_history_open: false,
      bucket_history_timeline: [],
      history_loading: false
    });

  render_bucket_history = () => {
    const { bucket_history_timeline, bucket_history_student_name, history_loading } = this.state;
    return (
      <div>
        <h3 style={{ marginBottom: '10px', color: 'var(--text-primary)' }}>
          Bucket history{bucket_history_student_name ? ` — ${bucket_history_student_name}` : ''}
        </h3>
        {history_loading ? (
          <div className="cb-hist-empty">Loading…</div>
        ) : bucket_history_timeline.length === 0 ? (
          <div className="cb-hist-empty">No history available.</div>
        ) : (
          <div className="cb-hist-list">
            {bucket_history_timeline.map((row) => {
              // Always show the event time in IST (Asia/Kolkata) so ops/sales/support
              // read a single canonical "when" regardless of their own CMS timezone.
              // moment-timezone renders the `z` token as "IST" for Asia/Kolkata.
              const at = row.created_date
                ? moment(row.created_date).tz('Asia/Kolkata').format('YYYY-MM-DD HH:mm:ss z')
                : '';
              const is_bucket_change = row.event_type === 'BUCKET_CHANGE';
              const main = is_bucket_change ? (
                <span>
                  <b>{humanize_bucket(row.from_bucket)}</b>
                  {' → '}
                  <b>{humanize_bucket(row.to_bucket)}</b>
                </span>
              ) : (
                <span>
                  <b>{humanize_event(row.event_type)}</b>
                  {': '}
                  {row.from_teacher_name || '--'}
                  {' → '}
                  {row.to_teacher_name || '--'}
                </span>
              );
              return (
                <div
                  className="cb-hist-row"
                  key={`${row.event_type}-${row.from_bucket || ''}-${row.to_bucket || ''}-${row.from_teacher_name || ''}-${row.to_teacher_name || ''}-${row.created_date}`}
                >
                  <span className="cb-hist-tag">{humanize_event(row.event_type)}</span>
                  <span className="cb-hist-body">
                    <div className="cb-hist-main">{main}</div>
                    {!is_bucket_change && (row.reason_tag || row.reason_description) ? (
                      <div className="cb-hist-desc">
                        {row.reason_tag ? <b>{row.reason_tag}</b> : null}
                        {row.reason_tag && row.reason_description ? ' — ' : null}
                        {row.reason_description || ''}
                      </div>
                    ) : null}
                    {at ? <div className="cb-hist-at">{at}</div> : null}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  render_filters = () => {
    const {
      selected_iana_timezone,
      iana_abbr_list,
      selected_pod,
      first_1on1_filter,
      selected_bucket
    } = this.state;
    const ctrl = 'w-full md:w-[210px]';
    return (
      <div className="mb-4 flex flex-col gap-3 rounded-xl border border-[var(--border-default)] bg-[var(--surface-card)] p-3 md:flex-row md:flex-wrap md:items-end">
        <div className={ctrl}>
          <CustomSingleSelect
            label="Bucket"
            options={this.get_bucket_options()}
            onChange={this.change_bucket}
            value={selected_bucket}
            style={{ width: '100%' }}
            showClear
            clear_fn={this.clear_bucket}
          />
        </div>
        <div className={ctrl}>
          <CustomSingleSelect
            label="Timezone"
            options={iana_abbr_list}
            onChange={this.change_iana_timezone}
            value={selected_iana_timezone}
            style={{ width: '100%' }}
            showClear
            clear_fn={this.clear_timezone}
          />
        </div>
        <div className={ctrl}>
          <CustomSingleSelect
            label="Pod"
            options={this.get_pod_options()}
            onChange={this.change_pod}
            value={selected_pod}
            style={{ width: '100%' }}
            showClear
            clear_fn={this.clear_pod}
          />
        </div>
        <div className={ctrl}>
          <CustomSingleSelect
            label="1st 1on1 Finished"
            options={[
              { value: 'ALL', label: 'ALL' },
              { value: 'YES', label: 'Yes' },
              { value: 'NO', label: 'No' }
            ]}
            onChange={this.change_first_1on1}
            value={first_1on1_filter}
            style={{ width: '100%' }}
            showClear
            clear_fn={this.clear_first_1on1}
          />
        </div>
      </div>
    );
  };

  clear_timezone = () => this.setState({ selected_iana_timezone: 'ALL' });

  clear_pod = () => this.setState({ selected_pod: 'ALL' });

  // resolve_pod_timezones maps each student's IANA timezone to a concierge pod via the pod region
  // resolver (goserver POST /concierge_pods/resolve_timezones, read-only API kept per decision 6):
  // Americas split East/West by tz_group, rest of world by tz→country→pod. Best-effort: on error
  // we leave the map empty and apply_pod_filter falls back to showing everyone.
  resolve_pod_timezones = (students_list) => {
    const timezones = [
      ...new Set((students_list || []).map((s) => s.iana_timezone).filter(Boolean))
    ];
    if (timezones.length === 0) {
      this.setState({ tz_region_map: {} });
      return;
    }
    post_api(get_api_url('/v2/cms_user/concierge_pods/resolve_timezones'), { timezones }, true)
      .then((res) => {
        const list = (res.data && res.data.data) || [];
        const tz_region_map = {};
        list.forEach((r) => {
          tz_region_map[r.timezone] = {
            region_id: r.region_id,
            region_name: r.region_name,
            resolved: r.resolved
          };
        });
        this.setState({ tz_region_map });
      })
      .catch(() => {
        this.setState({ tz_region_map: {} });
      });
  };

  get_pod_options = () => {
    const { pods } = this.state;
    return [
      { value: 'ALL', label: 'ALL Pods' },
      ...pods.map((pod) => ({ value: String(pod.id), label: pod.name })),
      { value: 'UNMAPPED', label: '(Unmapped)' }
    ];
  };

  apply_pod_filter = (students_list) => {
    const { selected_pod, tz_region_map } = this.state;
    if (!selected_pod || selected_pod === 'ALL') {
      return students_list;
    }
    // "(Unmapped)" bucket: no timezone, or a timezone that resolves to no pod (e.g. India, or a
    // country without a pod). Shown separately per PROD-6787 (1.a).
    if (selected_pod === 'UNMAPPED') {
      return students_list.filter((s) => {
        const entry = tz_region_map[s.iana_timezone];
        return !s.iana_timezone || !entry || !entry.resolved;
      });
    }
    // A specific pod: keep students whose timezone resolves to that pod's region id.
    const pod_id = Number(selected_pod);
    return students_list.filter((s) => {
      const entry = tz_region_map[s.iana_timezone];
      return !!entry && entry.resolved && entry.region_id === pod_id;
    });
  };

  open_history = (e, history) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    this.setState({ history, history_open: true });
  };

  open_chat = async (student) => {
    openChatWithUserUUID(student.uuid, UserRole.STUDENT);
  };

  activate_on_key = (e, fn) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fn();
    }
  };

  render_mobile_student_card = (student) => {
    const student_url = `/studentprofile?id=${student.uuid}`;
    const ethniCity =
      student.concierge_entered_ethnicity !== ''
        ? student.concierge_entered_ethnicity
        : student.ml_ethnicity;
    const tz = this.props.iana_timezone;
    const can_message =
      !!student.phone_no && student.phone_no !== '' && student.message_dispatch_method !== 'none';
    const is_wa = student.message_dispatch_method === 'whatsapp';
    const start_full = `${moment(student.created_date).tz(tz).format(day_format)}, ${moment(student.created_date).tz(tz).format(time_format)}`;
    const end_full = `${moment(student.trial_end_date).tz(tz).format(day_format)}, ${moment(student.trial_end_date).tz(tz).format(time_format)}`;
    const trialFull = `${start_full} → ${end_full}`;
    const start_short = moment(student.created_date).tz(tz).format(day_format);
    const end_short = moment(student.trial_end_date).tz(tz).format(day_format);
    const one_on_one = `${student.yoga_sessions_finshed}/${student.yoga_sessions_scheduled}`;
    const group = `${student.group_sessions_finshed}/${student.group_sessions_scheduled}`;
    const bucket_css = BUCKET_CSS[student.current_bucket] || '';
    const bucket_label = humanize_bucket(student.current_bucket);
    const bucket_at = this.fmt_bucket_date(student.entered_bucket_at);

    const member_badge = (
      <span className={`cb-badge ${student.is_member === 1 ? 'cb-yes' : 'cb-no'}`}>
        {student.is_member === 1 ? 'Yes' : 'No'}
      </span>
    );
    const replied_badge =
      student.to_student == 1 ? (
        <span className="cb-badge cb-yes">Yes</span>
      ) : student.to_student == 0 ? (
        <span className="cb-badge cb-no">No</span>
      ) : (
        <span className="cb-dash">&mdash;</span>
      );
    const advisor_el = student.sales_advisor_name;
    const bucket_cell = (
      <div className="cb-bucket">
        <div>
          <span className={`cb-bucket-pill ${bucket_css}`}>{bucket_label}</span>
        </div>
        <div className="bk-at">Entered: {bucket_at}</div>
      </div>
    );
    const edit_ico = (
      <span
        className="cb-edit"
        role="button"
        tabIndex={0}
        title="Edit ethnicity"
        onClick={() => this.open_ethnicity_popup(student)}
        onKeyDown={(e) => this.activate_on_key(e, () => this.open_ethnicity_popup(student))}
      >
        {EditIcon()}
      </span>
    );
    const history_link = student.health_history ? (
      <span
        className="cb-hist"
        role="button"
        tabIndex={0}
        onClick={(e) => this.open_history(e, student.health_history)}
        onKeyDown={(e) =>
          this.activate_on_key(e, () => this.open_history(null, student.health_history))
        }
      >
        {' · '}
        History
      </span>
    ) : null;
    const actions = (
      <div className="cb-acts">
        <button className="cb-btn cb-btn-chat" onClick={() => this.open_chat(student)}>
          Chat
        </button>
        <button className="cb-btn cb-btn-hist" onClick={() => this.open_bucket_history(student)}>
          History
        </button>
        {can_message ? (
          <button
            className={`cb-btn ${is_wa ? 'cb-btn-wa' : 'cb-btn-sms'}`}
            onClick={() => this.open_sms_window(student)}
          >
            <span className="cb-send">Send </span>
            {is_wa ? 'WA' : 'SMS'}
          </button>
        ) : null}
      </div>
    );

    return (
      <div className="cb-rec" key={student.uuid}>
        {/* ---- table row (container >= 820px) ---- */}
        <div className="cb-trow">
          <div className="cb-cell-name">
            <img
              src={student.profile_photo}
              onError={image_error_replace}
              alt=""
              className="cb-av"
            />
            <span className="id">
              <span className="cb-trunc" style={{ display: 'block' }}>
                <a className="cb-name" href={student_url} target="_blank" rel="noopener noreferrer">
                  {student.name}
                </a>{' '}
                {edit_ico}
              </span>
              <span className="cb-loc cb-trunc" style={{ display: 'block' }}>
                {student.iana_timezone}
                {ethniCity ? ` · ${ethniCity}` : ''}
                {history_link}
              </span>
              <span
                className="cb-conc-line cb-trunc"
                style={{ display: 'block' }}
                title={SALES_ADVISOR_TOOLTIP}
              >
                {advisor_el}
              </span>
            </span>
          </div>
          <div
            className="cb-stack cb-mono"
            style={{ fontSize: '11.5px', lineHeight: 1.5 }}
            title={trialFull}
          >
            <div className="a">{start_full}</div>
            <div className="b">{end_full}</div>
          </div>
          <div className="cb-center">{member_badge}</div>
          <div className="cb-sess cb-center cb-mono" style={{ fontSize: '12px', lineHeight: 1.6 }}>
            <div>1on1: {one_on_one}</div>
            <div>GC: {group}</div>
          </div>
          <div className="cb-center">{replied_badge}</div>
          <div className="cb-cell-bucket">{bucket_cell}</div>
          <div className="cb-cell-acts">{actions}</div>
        </div>

        {/* ---- boxed card (container < 820px) ---- */}
        <div className="cb-card">
          <div className="cb-card-hd">
            <img
              src={student.profile_photo}
              onError={image_error_replace}
              alt=""
              className="cb-av"
            />
            <span className="id">
              <span style={{ display: 'block' }}>
                <a className="cb-name" href={student_url} target="_blank" rel="noopener noreferrer">
                  {student.name}
                </a>{' '}
                {edit_ico}
              </span>
              <span className="cb-loc cb-trunc" style={{ display: 'block' }}>
                {student.iana_timezone}
                {history_link}
              </span>
              <span
                className="cb-conc-line cb-trunc"
                style={{ display: 'block' }}
                title={SALES_ADVISOR_TOOLTIP}
              >
                {advisor_el}
              </span>
            </span>
            {ethniCity ? <span className="cb-eth">{ethniCity}</span> : null}
          </div>
          <div className="cb-boxes">
            <div className="cb-box" title={trialFull}>
              <div className="bl">Trial</div>
              <div
                className="cb-mono"
                style={{ fontSize: '12px', fontWeight: 600, overflowWrap: 'anywhere' }}
              >
                {start_short} &rarr; {end_short}
              </div>
            </div>
            <div className="cb-box">
              <div className="bl">Member</div>
              <div className="bv">{member_badge}</div>
            </div>
            <div className="cb-box cb-box-sess">
              <div className="bl">Sessions fin/sch</div>
              <div className="cb-sessgrid cb-mono" style={{ fontSize: '13px' }}>
                <div>1on1: {one_on_one}</div>
                <div>GC: {group}</div>
              </div>
            </div>
            <div className="cb-box cb-box-act">
              <div className="bl">Replied</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {replied_badge}
                {actions}
              </div>
            </div>
          </div>
          <div className="cb-foot">
            <b>Bucket</b> <span className={`cb-bucket-pill ${bucket_css}`}>{bucket_label}</span>{' '}
            &nbsp;·&nbsp; <b>Entered</b> {bucket_at}
          </div>
        </div>
      </div>
    );
  };

  close_ethnicity_popup = () => {
    this.setState({
      open_ethnicity_popup: false
    });
  };

  open_ethnicity_popup = (student) => {
    this.setState({
      open_ethnicity_popup: true,
      student_info: student,
      concierge_entered_ethnicity: student.concierge_entered_ethnicity
    });
  };

  get_excel_data = (students) => {
    const teacherDataArr = [];
    students.forEach((item) => {
      const csvData = {
        name: item?.name,
        uuid: item?.uuid,
        hridaya_profile_link: `https://hridaya.myyogateacher.com/studentprofile?id=${item?.uuid}`,
        current_bucket: humanize_bucket(item?.current_bucket),
        entered_bucket_at: item?.entered_bucket_at
          ? moment(item?.entered_bucket_at)
              .tz(this.props.iana_timezone)
              .format('YYYY-MM-DD HH:mm:ss z')
          : '',
        first_1on1_session_status: this.get_first_1on1_status_label(
          item?.first_1on1_session_status
        ),
        email: item?.client_email,
        created_date: moment(item?.created_date)
          .tz(this.props.iana_timezone)
          .format('YYYY-MM-DD HH:mm:ss z'),
        funnel_url: item?.funnel_url,
        offer_type: item?.offer_type,
        funnel_type: item?.funnel_type,
        device_type: item?.device_type,
        yoga_level: item?.yoga_level,
        trial_end_date: moment(item?.trial_end_date)
          .tz(this.props.iana_timezone)
          .format('YYYY-MM-DD HH:mm:ss z'),
        credits: item?.credits,
        sales_advisor_name: item?.sales_advisor_name ?? '',
        is_member: item?.is_member,
        phone_personal: item?.phone_personal,
        yoga_sessions_finshed: item?.yoga_sessions_finshed,
        group_sessions_finshed: item?.group_sessions_finshed,
        yoga_sessions_scheduled: item?.yoga_sessions_scheduled,
        group_sessions_scheduled: item?.group_sessions_scheduled,
        iana_timezone: item?.iana_timezone,
        client_age: item?.client_age
      };
      teacherDataArr.push(csvData);
    });
    return teacherDataArr;
  };

  // Summary counts derive from the SAME post-filter array the cards render from, so the
  // total + per-bucket pills always match what's on screen as filters change.
  render_summary = (filter_students) => {
    const cards = conciergeBucketOptions.map((opt) => ({
      key: opt.value,
      label: opt.label,
      css: BUCKET_CSS[opt.value] || '',
      count: filter_students.filter((s) => s.current_bucket === opt.value).length
    }));
    return (
      <>
        <div className="cb-total">Total students: {filter_students.length}</div>
        <div className="cb-summary">
          {cards.map((c) => (
            <div className={`cb-count-pill ${c.css}`} key={c.key}>
              <span className="lbl">{c.label}</span>
              <span className="num">{c.count}</span>
            </div>
          ))}
        </div>
      </>
    );
  };

  render_data = () => {
    const { students, selected_iana_timezone, iana_abbr_list, first_1on1_filter, selected_bucket } =
      this.state;
    let filter_students = [...students];
    if (!!selected_iana_timezone && selected_iana_timezone !== 'ALL') {
      const timezone = iana_abbr_list.find((i) => i.label === selected_iana_timezone);
      if (timezone) {
        filter_students = filter_students.filter((s) => s.iana_timezone_abbr === timezone.value);
      }
    }
    filter_students = this.apply_pod_filter(filter_students);
    // 1st 1on1 finished filter (PROD-6787).
    if (first_1on1_filter !== 'ALL') {
      const want_finished = first_1on1_filter === 'YES';
      filter_students = filter_students.filter(
        (s) => (s.first_1on1_session_status === 'FINISHED') === want_finished
      );
    }
    // PROD-6988: bucket filter (client-side on current_bucket).
    if (selected_bucket && selected_bucket !== 'ALL') {
      filter_students = filter_students.filter((s) => s.current_bucket === selected_bucket);
    }
    // Summary counts/total derive from the FILTERED set (sort doesn't change counts).
    const summary = this.render_summary(filter_students);
    // PROD-6988: apply the chosen sort AFTER all filtering. The sorted array drives BOTH the
    // rendered cards and the Excel download, so the download reflects the displayed order.
    const sorted_students = this.sort_students(filter_students);
    const file_name = `concierge_post_trial_buckets_${moment().format('YYYY-MM-DD')}_${uuid()}`;
    return (
      <>
        {summary}
        <div className="cb-toolbar">
          <div className="cb-sortby">
            <CustomSingleSelect
              label="Sort by"
              options={conciergeBucketSortOptions}
              onChange={this.change_sort}
              value={this.state.sort_by}
              style={{ width: '100%' }}
              showClear
              clear_fn={this.clear_sort}
            />
          </div>
          <ExcelFile
            element={<button className="data-download-btn m-0">Download Data</button>}
            filename={file_name}
          >
            <ExcelSheet data={this.get_excel_data(sorted_students)} name="Latest Students">
              {conciergeBucketDownloadList.map((d) => (
                <ExcelColumn key={d.value} label={d.label} value={d.value} />
              ))}
            </ExcelSheet>
          </ExcelFile>
        </div>

        {/* Lead-management list: dense aligned table on desktop/laptop, boxed card
            on tablet/mobile (container-query toggle at 820px). ONE record per row. */}
        <div className="cb-list">
          <div className="cb-legend">
            <span>
              <span className="dot dot-p" />
              Primary — Trial · Member · Sessions · Replied
            </span>
            <span className="r">sessions shown as fin / sch</span>
          </div>
          <div className="cb-thead">
            <div>Name</div>
            <div>Trial</div>
            <div className="cb-center">Member</div>
            <div className="cb-center">Sessions</div>
            <div className="cb-center">Replied</div>
            <div>Bucket</div>
            <div className="rh">Actions</div>
          </div>
          {sorted_students.map(this.render_mobile_student_card)}
        </div>
      </>
    );
  };

  close_history = () => this.setState({ history_open: false, open_info: false, student_info: {} });

  open_sms_window = (student) => {
    this.setState({
      sms_window_open: true,
      sms_student: student.uuid,
      sms_student_name: student.name,
      original_message_uuid: student.sms_uuid,
      message_dispatch_method: student.message_dispatch_method,
      is_allowed_whatsapp: student.is_allowed_whatsapp,
      is_allowed_sms: student.is_allowed_sms
    });
  };

  on_reply_sent = () => {
    const { students, sms_student } = this.state;
    const new_students_data = [...students];
    const student = new_students_data.find((s) => s.uuid === sms_student);
    if (student) {
      student.to_student = 1;
      this.setState({ students: new_students_data });
    }
  };

  close_sms_window = () => this.setState({ sms_window_open: false, sms_student: '' });

  render_info = () => {
    const { student_info } = this.state;
    return (
      <div>
        <p>Name - {student_info.name}</p>
        <p>Affiliate - {student_info.affiliate}</p>
        <p>Queue Table Affiliate - {student_info.queue_affiliate}</p>
        <p>
          Trial End -{' '}
          {moment(student_info.trial_end_date).tz(this.props.iana_timezone).format(day_format)}{' '}
          {moment(student_info.trial_end_date).tz(this.props.iana_timezone).format(time_format)}
        </p>
      </div>
    );
  };

  change_ethnicity_value = (e) => {
    e.preventDefault();
    this.setState({ concierge_entered_ethnicity: e.target.value });
  };

  submit_entered_ethnicity = async () => {
    this.setState({ loading: true });
    const url = get_api_url('/v2/cms_user/update_student_ethnicity', false, true);
    const payload = {
      student_uuid: this.state.student_info?.uuid,
      ethnicity: this.state.concierge_entered_ethnicity
    };
    try {
      await put_api(url, payload, true);
      this.setState({ loading: false });
      this.load_data();
      this.close_ethnicity_popup();
    } catch (e) {
      let err_message;
      try {
        err_message = e.response.data.message;
      } catch (err) {
        err_message = 'Some error occured. Please contact dev team.';
      }
      this.setState({ loading: false });
    }
  };

  render_change_ethnicity = () => (
    <>
      <CustomSingleSelect
        label="Ethnicity"
        options={EthnicityOptions}
        onChange={(e) => this.change_ethnicity_value(e)}
        value={this.state.concierge_entered_ethnicity}
        name="concierge_entered_ethnicity"
      />
      <button className="ct-small-btn" onClick={() => this.submit_entered_ethnicity()}>
        Submit
      </button>
    </>
  );

  render() {
    const {
      loading,
      history_open,
      history,
      open_info,
      sms_window_open,
      sms_student,
      sms_student_name,
      original_message_uuid,
      open_ethnicity_popup,
      student_info,
      message_dispatch_method,
      is_allowed_sms,
      is_allowed_whatsapp,
      bucket_history_open
    } = this.state;
    return (
      <CommonHeader loading={loading} title="Concierge Post-Trial Buckets: MyYogaTeacher">
        <CustomModal show={history_open} close={this.close_history}>
          <div>{history}</div>
        </CustomModal>
        <CustomModal show={open_info} close={this.close_history}>
          {this.render_info()}
        </CustomModal>
        <div className="cb-scope" style={{ minHeight: '400px' }}>
          {this.render_filters()}
          {this.render_data()}
        </div>
        {sms_window_open ? (
          <SmsWindow
            student_uuid={sms_student}
            student_name={sms_student_name}
            original_message_uuid={original_message_uuid}
            close_fn={this.close_sms_window}
            send_success_notification={this.on_reply_sent}
            message_dispatch_method={message_dispatch_method}
            is_allowed_whatsapp={is_allowed_whatsapp}
            is_allowed_sms={is_allowed_sms}
          />
        ) : null}
        <CustomModal show={bucket_history_open} close={this.close_bucket_history}>
          {bucket_history_open ? this.render_bucket_history() : null}
        </CustomModal>
        <CustomModal show={open_ethnicity_popup} close={this.close_ethnicity_popup}>
          {this.render_change_ethnicity(student_info)}
        </CustomModal>
      </CommonHeader>
    );
  }
}

const mapStateToProps = (state) => ({
  iana_timezone: state.home.iana_timezone
});

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(ConciergePostTrialBuckets);
